import React, { useState, useEffect } from "react";
import { RegisterButton } from "../Buttons";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUpdatePasswordSuccess,
  updatePasswordRequest,
} from "../../redux/action";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaCheckCircle,
  FaKey,
} from "react-icons/fa";

// --- Reusable Password Input with Toggle ---
const PasswordField = ({ label, name, placeholder, formik }) => {
  const [showPassword, setShowPassword] = useState(false);
  const hasError = formik.touched[name] && formik.errors[name];
  const isValid = formik.touched[name] && !hasError && formik.values[name];

  return (
    <div className="w-full">
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaKey
            className={`text-gray-400 ${hasError ? "text-red-400" : ""} ${
              isValid ? "text-green-500" : ""
            }`}
          />
        </div>

        <input
          type={showPassword ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className={`
            w-full pl-10 pr-10 py-3 rounded-lg border-2 transition-all outline-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500 placeholder-red-300"
                : isValid
                ? "border-green-200 bg-green-50 text-gray-700 focus:border-green-500"
                : " bg-gray-50 text-gray-700 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10"
            }
          `}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer outline-none"
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>

      {hasError && (
        <div className="mt-1 text-xs text-red-500 font-medium animate-pulse">
          {formik.errors[name]}
        </div>
      )}
    </div>
  );
};

export const UpdatePasswordForm = () => {
  const dispatch = useDispatch();

  const {
    updatePasswordSuccess,
    updatePasswordError,
    updatePasswordLoading,
    token,
  } = useSelector((state) => ({
    updatePasswordSuccess: state.ajaxStatuses.success.updatePassword,
    updatePasswordError: state.ajaxStatuses.errors.updatePassword,
    updatePasswordLoading: state.loadingIndicator.updatePasswordLoading,
    token: state.userData.token,
  }));

  // Moved Confirm Password logic into Yup for real-time validation
  const formik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      password: yup.string().required("Current password is required"),
      newPassword: yup
        .string()
        .min(8, "Password must be at least 8 characters")
        .required("New Password is required"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("newPassword"), null], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      // Remove confirmPassword before sending to API if backend doesn't expect it
      const { confirmPassword, ...submitData } = values;
      dispatch(updatePasswordRequest({ formData: submitData, token }));
    },
  });

  useEffect(() => {
    if (updatePasswordError) {
      toast.error(updatePasswordError);
    }
  }, [updatePasswordError]);

  useEffect(() => {
    if (updatePasswordSuccess) {
      toast.success("Password updated successfully");
      formik.resetForm();
      dispatch(clearUpdatePasswordSuccess());
    }
  }, [updatePasswordSuccess, dispatch, formik]);

  return (
    <div className="max-w-4xl mx-auto mt-8">
      <div className="bg-white rounded-2xl shadow-sm border  overflow-hidden">
        {/* Header Section */}
        <div className="bg-gray-50/50 px-8 py-6 border-b  flex items-center gap-4">
          <div className="bg-orange-100 p-3 rounded-full text-orange-600">
            <FaShieldAlt className="text-xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800 font-heading">
              Security Settings
            </h2>
            <p className="text-sm text-gray-500">
              Manage your password and account security
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3">
          {/* Main Form */}
          <div className="lg:col-span-2 p-6 md:p-8 border-r ">
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <PasswordField
                label="Current Password"
                name="password"
                placeholder="Enter current password"
                formik={formik}
              />

              <div className="h-px bg-gray-100 w-full my-4"></div>

              <div className="space-y-4">
                <PasswordField
                  label="New Password"
                  name="newPassword"
                  placeholder="Enter new password"
                  formik={formik}
                />

                <PasswordField
                  label="Confirm New Password"
                  name="confirmPassword"
                  placeholder="Re-enter new password"
                  formik={formik}
                />
              </div>

              <div className="pt-4">
                <RegisterButton
                  loading={updatePasswordLoading}
                  disable={!(formik.dirty && formik.isValid)}
                  action={formik.handleSubmit}
                  text={
                    <div className="flex items-center justify-center gap-2">
                      <FaLock /> Update Password
                    </div>
                  }
                />
              </div>
            </form>
          </div>

          {/* Sidebar: Password Requirements */}
          <div className="lg:col-span-1 bg-gray-50/30 p-6 md:p-8">
            <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
              Password Requirements
            </h4>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <FaCheckCircle className="text-green-500 mt-0.5 text-xs" />
                <span>Minimum 8 characters long</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="text-green-500 mt-0.5 text-xs" />
                <span>At least one number</span>
              </li>
              <li className="flex items-start gap-2">
                <FaCheckCircle className="text-green-500 mt-0.5 text-xs" />
                <span>At least one special character</span>
              </li>
            </ul>

            <div className="mt-8 bg-primary-50 border border-primary-100 rounded-lg p-4">
              <p className="text-xs text-primary-700 leading-relaxed">
                <strong>Security Tip:</strong> Never share your password with
                anyone. Our support team will never ask for your password.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
